<script setup>
// Botão CDC "Procurar no dashboard". Self-gate por `value` (UI-01). Busca o
// cliente card correspondente ao contato (contact_id + field literal) e
// delega o resultado (aba única / modal / not_found) para
// CardSearchResultDialog, o único dono da lógica de abertura de resultado
// (Phase 9). Erro sempre mostra o toast genérico CDC.CARD_SEARCH.toast.error
// (UI-05). Origem: ADR 0008.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import CardDashboardSearchAPI from '../api/cardDashboardSearch';
import CardSearchResultDialog from './CardSearchResultDialog.vue';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
  field: { type: String, required: true },
  value: { type: String, default: '' },
});

const { t } = useI18n();

const isLoading = ref(false);
const resultDialogRef = ref(null);

const shouldRender = computed(() => Boolean(props.value));

const onClick = async () => {
  if (isLoading.value) return;
  isLoading.value = true;
  try {
    const response = await CardDashboardSearchAPI.search(
      props.contactId,
      props.field
    );
    const list = response.data;
    if (!Array.isArray(list)) {
      throw new Error('Unexpected card dashboard search response shape');
    }
    if (list.length === 0) {
      useAlert(t('CDC.CARD_SEARCH.toast.not_found'));
    } else {
      resultDialogRef.value?.present(list);
    }
  } catch (error) {
    useAlert(t('CDC.CARD_SEARCH.toast.error'));
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <span class="contents">
    <NextButton
      v-if="shouldRender"
      v-tooltip.top-end="t('CDC.CARD_SEARCH.tooltip')"
      icon="i-lucide-external-link"
      ghost
      xs
      slate
      class="ltr:-ml-1 rtl:-mr-1 opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100 transition-opacity"
      :disabled="isLoading"
      :is-loading="isLoading"
      @click.prevent="onClick"
    />
    <!--
      .prevent is load-bearing: this slot lives inside ContactInfoRow.vue's
      anchor element, so a plain click handler would also follow the row's
      tel:/mailto: href and hand the click to the OS dialer or mail client.
    -->
    <CardSearchResultDialog v-if="shouldRender" ref="resultDialogRef" />
  </span>
</template>
